import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import AdminLayout, { KPICard, StatusBadge } from "../../components/admin/AdminLayout";
import { campaignDetail, EMAIL_STATUS } from "../../lib/admin-api";
import { ArrowLeft, Send, AlertTriangle, Clock, Users } from "lucide-react";

const STATUS_LABELS = {
    draft: { label: "Borrador", color: "bg-neutral-100 text-neutral-600" },
    scheduled: { label: "Programada", color: "bg-amber-100 text-amber-700" },
    sending: { label: "Enviando", color: "bg-blue-100 text-blue-700" },
    sent: { label: "Enviada", color: "bg-green-100 text-green-700" },
    failed: { label: "Fallida", color: "bg-red-100 text-red-700" },
};

export default function AdminCampaignDetail() {
    const { id } = useParams();
    const [data, setData] = useState(null);
    const [tab, setTab] = useState("resumen");

    useEffect(() => {
        let alive = true;
        campaignDetail(id).then((d) => { if (alive) setData(d); }).catch(console.error);
        return () => { alive = false; };
    }, [id]);

    if (!data) return <AdminLayout title="Cargando…"></AdminLayout>;
    const c = data.campaign;
    const st = STATUS_LABELS[c.status] || { label: c.status, color: "bg-neutral-100" };
    const successRate = (c.sent_count + c.failed_count) > 0 ? Math.round((c.sent_count / (c.sent_count + c.failed_count)) * 100) : 0;

    return (
        <AdminLayout
            title={c.name}
            subtitle={c.subject_a}
            testid="admin-campaign-detail"
            actions={
                <div className="flex gap-2 items-center">
                    <StatusBadge label={st.label} color={st.color} />
                    {c.status === "draft" && <Link to={`/admin/campaigns/${id}/edit`} className="h-9 px-3 bg-eclipse-cta text-white text-xs font-semibold rounded-md">Editar</Link>}
                    <Link to="/admin/campaigns" className="h-9 px-3 border border-neutral-200 rounded-md text-sm flex items-center gap-2 bg-white"><ArrowLeft className="w-4 h-4" /> Volver</Link>
                </div>
            }
        >
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <KPICard label="Enviados" value={c.sent_count || 0} icon={Send} accent="text-green-600" testid="cd-sent" />
                <KPICard label="Fallidos" value={c.failed_count || 0} icon={AlertTriangle} accent="text-red-600" testid="cd-failed" />
                <KPICard label="Skipped" value={c.skipped_count || 0} icon={Clock} accent="text-neutral-500" testid="cd-skipped" />
                <KPICard label="Tasa éxito" value={`${successRate}%`} icon={Users} accent="text-eclipse-cta" testid="cd-success" />
            </div>

            <div className="flex gap-1 bg-neutral-100 p-0.5 rounded w-fit">
                {["resumen", "destinatarios", "preview"].map((t) => (
                    <button key={t} onClick={() => setTab(t)} className={`px-4 py-1.5 text-xs rounded ${tab === t ? "bg-white shadow-sm font-semibold" : "text-neutral-500"}`} data-testid={`cd-tab-${t}`}>
                        {t === "resumen" ? "Resumen" : t === "destinatarios" ? "Destinatarios" : "Preview"}
                    </button>
                ))}
            </div>

            {tab === "resumen" && (
                <div className="bg-white border border-neutral-200 rounded-md p-5 space-y-3 text-sm">
                    <Row label="Nombre">{c.name}</Row>
                    <Row label="Asunto A">{c.subject_a}</Row>
                    {c.subject_b && <Row label="Asunto B">{c.subject_b}</Row>}
                    {c.ab_split > 0 && <Row label="A/B split">{c.ab_split}% al variant B</Row>}
                    <Row label="Estado"><StatusBadge label={st.label} color={st.color} /></Row>
                    <Row label="Creada">{new Date(c.created_at).toLocaleString("es-MX")}</Row>
                    {c.scheduled_at && <Row label="Programada">{new Date(c.scheduled_at).toLocaleString("es-MX")}</Row>}
                    {c.sent_at && <Row label="Enviada">{new Date(c.sent_at).toLocaleString("es-MX")}</Row>}
                    <Row label="Segmentación">
                        <pre className="text-xs bg-neutral-50 p-3 rounded border border-neutral-100">{JSON.stringify(c.segments || {}, null, 2)}</pre>
                    </Row>
                </div>
            )}

            {tab === "destinatarios" && (
                <div className="bg-white border border-neutral-200 rounded-md overflow-hidden">
                    <table className="w-full text-sm">
                        <thead className="bg-neutral-50 text-neutral-600 text-[11px] uppercase tracking-wider">
                            <tr>
                                <th className="text-left px-4 py-3">Fecha</th>
                                <th className="text-left px-4 py-3">Destinatario</th>
                                <th className="text-left px-4 py-3">Asunto</th>
                                <th className="text-left px-4 py-3">Variant</th>
                                <th className="text-left px-4 py-3">Estado</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(data.emails || []).length === 0 && <tr><td colSpan={5} className="text-center py-10 text-neutral-400">Aún no hay envíos</td></tr>}
                            {(data.emails || []).map((e) => (
                                <tr key={e.id} className="border-t border-neutral-100">
                                    <td className="px-4 py-2.5 text-neutral-500 text-xs">{new Date(e.created_at).toLocaleString("es-MX")}</td>
                                    <td className="px-4 py-2.5 font-medium">{e.destinatario}</td>
                                    <td className="px-4 py-2.5 max-w-xs truncate text-neutral-700">{e.asunto}</td>
                                    <td className="px-4 py-2.5 text-xs text-neutral-500">{e.origen}</td>
                                    <td className="px-4 py-2.5"><StatusBadge label={EMAIL_STATUS[e.estado]?.label || e.estado} color={EMAIL_STATUS[e.estado]?.color || "bg-neutral-100"} /></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {tab === "preview" && (
                <div className="bg-white border border-neutral-200 rounded-md overflow-hidden">
                    <div className="bg-neutral-50 px-4 py-3 border-b text-xs">
                        <div className="text-neutral-500">Asunto:</div>
                        <div className="font-semibold mt-0.5">{c.subject_a}</div>
                    </div>
                    <div className="p-5 prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: (c.content || "").replace(/\{\{nombre\}\}/g, "[NOMBRE]") }} />
                </div>
            )}
        </AdminLayout>
    );
}

function Row({ label, children }) {
    return (
        <div className="grid grid-cols-3 gap-4 py-1.5 border-b border-neutral-100 last:border-0">
            <div className="text-neutral-500 text-xs uppercase tracking-wider">{label}</div>
            <div className="col-span-2 text-neutral-900">{children}</div>
        </div>
    );
}
